import dayjs from 'dayjs'
import NextLink from 'next/link'
import React from 'react'
import styled from 'styled-components'
import { StoryFieldsFragment } from 'types/graphql'
import { resizedImageUrl } from 'utils/imageUrl'
import { media } from 'utils/media'
import { Link } from 'lib/Link'
import { StoryAuthor } from 'lib/StoryAuthor'
import { Paragraph, textWeight, textSize } from 'lib/Text'

const Container = styled.article`
  padding: 24px 48px 24px 8px;
  display: grid;
  grid-template-columns: 2fr 4fr;
  grid-gap: 24px;

  &:last-child {
    text-decoration: none;
    margin-bottom: 64px;
  }

  ${media.desktopBoundsDown} {
    padding: var(--spacing);
  }

  ${media.phoneDown} {
    grid-gap: 0;
    /* padding: 20px; */

    &:last-child {
      margin-bottom: 0;
    }
  }
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  ${media.phoneDown} {
    grid-column: span 2;
  }
`

const TitleContainer = styled.h2`
  margin-bottom: 12px;
`

const PublicationDate = styled.time`
  ${textWeight('regular')}
  ${textSize('pxs')}
  color: var(--neutral-60);
  margin-bottom: 2px;
`

const Title = styled(Link)`
  ${textWeight('semi')}
  ${textSize('l')}
`

const ImageContainer = styled.a`
  background-color: var(--neutral-10);
  border: var(--border);
  display: block;
  padding-bottom: 100%;
  position: relative;
  ${media.phoneDown} {
    grid-column: span 2;
    width: 100%;
    height: 200px;
    padding-bottom: 0;
  }
`

const StoryImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  box-sizing: border-box;
  padding: 0;
  border: none;
  margin: auto;
  display: block;
  width: 0;
  height: 0;
  min-width: 100%;
  max-width: 100%;
  min-height: 100%;
  max-height: 100%;
  object-fit: cover;
  opacity: 0;
  transition: opacity 100ms ease-in;

  &.lazyloaded,
  &.preloaded {
    opacity: 1;
  }
`

interface Props {
  className?: string
  lazyLoadImage?: boolean
  story: StoryFieldsFragment
}

const IMAGE_SIZE = 600

export const Story = ({ story, className, lazyLoadImage }: Props) => {
  const imageUrl1x = resizedImageUrl(story.imageFullPath, { width: IMAGE_SIZE, height: IMAGE_SIZE })
  const imageUrl2x = resizedImageUrl(story.imageFullPath, { width: IMAGE_SIZE * 2, height: IMAGE_SIZE * 2 })

  return (
    <Container className={className}>
      <NextLink legacyBehavior href={`/stories/${story.slug}`} passHref>
        <ImageContainer>
          <StoryImage
            alt={story.title ?? ''}
            src={lazyLoadImage ? undefined : imageUrl1x}
            srcSet={lazyLoadImage ? undefined : `${imageUrl1x} 1x, ${imageUrl2x} 2x`}
            data-srcset={`${imageUrl1x} 1x, ${imageUrl2x} 2x`}
            className={lazyLoadImage ? 'lazyload' : 'preloaded'}
          />
        </ImageContainer>
      </NextLink>
      <Content>
        {story.publishedAt && (
          <PublicationDate dateTime={dayjs(story.publishedAt).format('YYYY-MM-DD')} title={dayjs(story.publishedAt).format('DD/MM/YYYY')}>
            {dayjs(story.publishedAt).format('MMMM D, YYYY')}
          </PublicationDate>
        )}
        <TitleContainer>
          <Title href={`/stories/${story.slug}`} underlineOnNormalState={false}>
            {story.title}
          </Title>
        </TitleContainer>
        <Paragraph>{story.metaDescription}</Paragraph>
        <StoryAuthor
          imageUrl={resizedImageUrl(story.author.avatarFullPath, { width: 180, height: 180 })}
          name={story.author.fullName}
          title={story.author.jobTitle}
          username={story.author.username}
        />
      </Content>
    </Container>
  )
}
